import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Menu,
  ListItemIcon,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  CircularProgress,
  useTheme,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  Download,
  Print,
  Refresh,
  Search as SearchIcon,
  Cancel as CancelIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle,
  Pending,
  Receipt,
  Phone,
  Email,
  Visibility,
  Close,
  Person,
  Home,
  MoneyOff,
  Business,
  Warning,
  PictureAsPdf,
} from '@mui/icons-material';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import { getAssociates } from '../../services/associate';
import { getCancelledBookings } from '../../services/reports';

const CancelReport = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    cancelReason: '',
    associate: '',
    searchTerm: '',
  });
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [cancellationChargePercent, setCancellationChargePercent] = useState('');
  const [viewTabValue, setViewTabValue] = useState(0);
  const [associates, setAssociates] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  const [associateSearchInput, setAssociateSearchInput] = useState("");
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const associateSearchTimeoutRef = useRef(null);

  const fetchAssociates = async (searchKey = '') => {
    setAssociatesLoading(true);
    try {
      const params = { limit: 10 };
      if (searchKey && searchKey.trim().length > 0) {
        params.search = searchKey.trim();
        params.searchKey = searchKey.trim();
        params.filterKey = searchKey.trim();
      }
      const response = await getAssociates(params);
      let associatesData = [];
      if (response && response.data) {
        associatesData = Array.isArray(response.data) ? response.data : (response.data.docs || []);
      } else if (Array.isArray(response)) {
        associatesData = response;
      }
      const formattedAssociates = associatesData.map(associate => ({
        id: associate.id || associate._id || associate.associateId,
        name: associate.name || associate.fullName || `${associate.firstName || ''} ${associate.lastName || ''}`.trim(),
        email: associate.email || '',
        associateId: associate.associateId || associate.id || associate._id,
        ...associate
      }));
      setAssociates(formattedAssociates);
    } catch (error) {
      console.error('Failed to fetch associates:', error);
      setAssociates([]);
    } finally {
      setAssociatesLoading(false);
    }
  };

  useEffect(() => {
    fetchAssociates();
  }, []);

  useEffect(() => {
    fetchCancelledBookings({ page: 1, limit: 10 });
  }, []);

  const fetchCancelledBookings = async (searchParams = {}) => {
    setBookingsLoading(true);
    try {
      const params = {
        fromDate: searchParams.fromDate !== undefined ? searchParams.fromDate : filters.fromDate,
        toDate: searchParams.toDate !== undefined ? searchParams.toDate : filters.toDate,
        searchKey: searchParams.searchTerm !== undefined ? searchParams.searchTerm : filters.searchTerm,
        associateId: searchParams.associateId !== undefined ? searchParams.associateId : (selectedAssociate ? (selectedAssociate._id || selectedAssociate.id) : ''),
        page: searchParams.page || page + 1,
        limit: searchParams.limit || rowsPerPage,
      };
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await getCancelledBookings(params);
      if (response.status && response.data) {
        const bookings = response.data.docs || [];
        const transformedBookings = bookings.map((booking, index) => {
          return {
            sno: ((response.data.page || 1) - 1) * (response.data.limit || 10) + index + 1,
            bookingId: `BK${booking.bookingId || booking._id?.slice(-4) || index + 1}`,
            bookingDate: booking.bookingDate ? new Date(booking.bookingDate).toISOString().split('T')[0] : '',
            cancelDate: booking.cancelledAt ? new Date(booking.cancelledAt).toISOString().split('T')[0] : (booking.approvedDate ? new Date(booking.approvedDate).toISOString().split('T')[0] : ''),
            customerName: booking.customer?.name || '',
            customerPhone: booking.customer?.phone || '',
            plotNo: booking.particular?.inventoryDetail || '',
            plotAmount: booking.propertyAmount || 0,
            paidAmount: booking.totalReceived || 0,
            cancellationCharge: booking.cancellationChargeAmount ?? Math.round((booking.totalReceived || 0) * 0.05),
            refundAmount: booking.refundedAmount ?? Math.max(0, (booking.totalReceived || 0) - Math.round((booking.totalReceived || 0) * 0.05)),
            cancelReason: booking.cancellationReason || booking.remark || 'Not specified',
            refundMethod: booking.refundMethod || booking.mode || 'Not specified',
            status: 'Cancelled',
            associate: booking.associate?.name || '',
            cancelledBy: booking.cancelledBy || booking.updatedBy || 'System',
            associateId: booking.associate?.associateId || '',
            paymentReceipts: [],
            refunds: []
          };
        });

        setCancelledBookings(transformedBookings);
        setTotalBookings(response.data.totalDocs || 0);

        if (!hasSearched) {
          setHasSearched(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch cancelled bookings:', error);
      setCancelledBookings([]);
      setTotalBookings(0);
    } finally {
      setBookingsLoading(false);
    }
  };


  const customerSuggestions = useMemo(() => {
    const uniqueCustomers = [];
    const customerMap = new Map();

    (hasSearched ? cancelledBookings : []).forEach(booking => {
      if (!customerMap.has(booking.customerName)) {
        customerMap.set(booking.customerName, {
          name: booking.customerName,
          phone: booking.customerPhone || '',
          bookingId: booking.bookingId,
          plotNo: booking.plotNo,
          associate: booking.associate || ''
        });
      }
    });

    return Array.from(customerMap.values());
  }, [cancelledBookings, hasSearched]);

  const cancelReasons = [
    'Customer Request',
    'Payment Default',
    'Document Verification Failed',
    'Policy Violation',
    'Duplicate Booking',
    'Administrative Error',
    'Other',
  ];

  const handleSearch = async () => {
    setSearchLoading(true);
    setHasSearched(true);
    setPage(0);
    try {
      await fetchCancelledBookings({ page: 1 });
    } catch (error) {
      console.error('Error fetching cancelled bookings:', error);
    }
    setSearchLoading(false);
  };

  const handleClearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      cancelReason: '',
      associate: '',
      searchTerm: '',
    });
    setSelectedAssociate(null);
    setAssociateSearchInput('');
    setHasSearched(true);
    setPage(0);
    setCancelledBookings([]);

    // Reload initial data
    fetchCancelledBookings({
      page: 1,
      limit: rowsPerPage,
      searchTerm: '',
      fromDate: '',
      toDate: '',
      associateId: ''
    });
  };

  const buildExportRows = () => {
    const exportData = hasSearched ? cancelledBookings : getFilteredData();
    return exportData.map((booking, i) => ({
      'S.No': i + 1,
      'Booking ID': booking.bookingId,
      'Booking Date': booking.bookingDate,
      'Cancel Date': booking.cancelDate,
      'Customer Name': booking.customerName,
      'Property No': booking.plotNo,
      'Property Amount': booking.plotAmount || 0,
      'Paid Amount': booking.paidAmount || 0,
      'Refund Amount': booking.refundAmount || 0,
      'Cancel Reason': booking.cancelReason,
      'Associate': booking.associate,
      'Cancelled By': booking.cancelledBy,
    }));
  };

  const getExportFileName = () => `cancel_report_${new Date().toISOString().slice(0, 10)}`;

  const handleExportCsv = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsCSV({ rows, fileNameBase: getExportFileName() });
  };

  const handleExportPdf = () => {
    const rows = buildExportRows();
    if (!rows.length) return;

    const formattedRows = rows.map(r => ({
      ...r,
      'Property Amount': `₹${r['Property Amount'].toLocaleString()}`,
      'Paid Amount': `₹${r['Paid Amount'].toLocaleString()}`,
      'Refund Amount': `₹${r['Refund Amount'].toLocaleString()}`,
    }));

    exportAsPDF({
      rows: formattedRows,
      title: 'Cancel Booking Report',
      fileNameBase: getExportFileName(),
      orientation: 'l'
    });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsExcel({
      rows,
      sheetName: 'Cancel Bookings',
      fileNameBase: getExportFileName()
    });
  };

  const handleView = (booking) => {
    setSelectedBooking(booking);
    if (booking.cancellationCharge && booking.paidAmount) {
      const calculatedPercent = ((booking.cancellationCharge / booking.paidAmount) * 100).toFixed(2);
      setCancellationChargePercent(calculatedPercent);
    } else {
      setCancellationChargePercent('');
    }
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedBooking(null);
    setCancellationChargePercent('');
    setViewTabValue(0);
  };

  const getFilteredData = useCallback(() => {
    if (!hasSearched && cancelledBookings.length === 0) {
      return [];
    }

    let filteredData = [...cancelledBookings];

    // Apply customer search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.customerName.toLowerCase().includes(searchLower) ||
        item.customerPhone?.includes(filters.searchTerm) ||
        item.bookingId.toLowerCase().includes(searchLower) ||
        item.plotNo.toLowerCase().includes(searchLower) ||
        (item.associate && item.associate.toLowerCase().includes(searchLower))
      );
    }

    // Apply date filters (cancel date)
    if (filters.fromDate) {
      filteredData = filteredData.filter(item => item.cancelDate >= filters.fromDate);
    }
    if (filters.toDate) {
      filteredData = filteredData.filter(item => item.cancelDate <= filters.toDate);
    }

    // Apply cancel reason filter
    if (filters.cancelReason) {
      filteredData = filteredData.filter(item => item.cancelReason === filters.cancelReason);
    }

    // Apply associate filter
    if (filters.associate) {
      filteredData = filteredData.filter(item => item.associate === filters.associate);
    }

    return filteredData;
  }, [hasSearched, filters.searchTerm, filters.fromDate, filters.toDate, filters.cancelReason, filters.associate, cancelledBookings]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData, cancelledBookings, hasSearched]);

  const totalCancelled = hasSearched ? totalBookings : filteredData.length;
  const totalRefundAmount = (hasSearched ? cancelledBookings : filteredData).reduce((sum, item) => sum + item.refundAmount, 0);
  const totalCancellationCharges = (hasSearched ? cancelledBookings : filteredData).reduce((sum, item) => sum + item.cancellationCharge, 0);
  const totalPaidAmount = (hasSearched ? cancelledBookings : filteredData).reduce((sum, item) => sum + item.paidAmount, 0);

  const paginatedData = hasSearched ? cancelledBookings : filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 2, mr: 5, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Cancel Booking Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage cancelled bookings
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ExportButton
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              onExportExcel={handleExportExcel}
            />
            <RefreshButton onClick={() => fetchCancelledBookings()} loading={bookingsLoading} />
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            p: 3, 
            textAlign: 'center', 
            bgcolor: '#fff',
            boxShadow: '0 4px 20px rgba(211, 47, 47, 0.15)',
            background: 'linear-gradient(135deg, #fff 0%, #fff5f5 100%)',
            border: '1px solid rgba(211, 47, 47, 0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(211, 47, 47, 0.25)',
            }
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(211, 47, 47, 0.1)',
              mb: 2
            }}>
              <CancelIcon sx={{ fontSize: 32, color: '#d32f2f' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                {totalCancelled}
              </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Total Cancelled
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            p: 3, 
            textAlign: 'center', 
            bgcolor: '#fff',
            boxShadow: '0 4px 20px rgba(46, 125, 50, 0.15)',
            background: 'linear-gradient(135deg, #fff 0%, #f1f8f4 100%)',
            border: '1px solid rgba(46, 125, 50, 0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(46, 125, 50, 0.25)',
            }
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(46, 125, 50, 0.1)',
              mb: 2
            }}>
              <AccountBalanceIcon sx={{ fontSize: 32, color: '#2e7d32' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                ₹{(totalRefundAmount / 100000).toFixed(2)}L
              </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Total Refund Amount
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            p: 3, 
            textAlign: 'center', 
            bgcolor: '#fff',
            boxShadow: '0 4px 20px rgba(211, 47, 47, 0.15)',
            background: 'linear-gradient(135deg, #fff 0%, #fff5f5 100%)',
            border: '1px solid rgba(211, 47, 47, 0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(211, 47, 47, 0.25)',
            }
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(211, 47, 47, 0.1)',
              mb: 2
            }}>
              <CurrencyRupeeIcon sx={{ fontSize: 32, color: '#d32f2f' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                ₹{(totalCancellationCharges / 1000).toFixed(0)}K
              </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Total Cancellation Charges
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: 3, 
            p: 3, 
            textAlign: 'center', 
            bgcolor: '#fff',
            boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)',
            background: 'linear-gradient(135deg, #fff 0%, #f3f7ff 100%)',
            border: '1px solid rgba(25, 118, 210, 0.1)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 30px rgba(25, 118, 210, 0.25)',
            }
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              mb: 2
            }}>
              <CheckCircleIcon sx={{ fontSize: 32, color: '#1976d2' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                ₹{(totalPaidAmount / 100000).toFixed(2)}L
              </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Total Paid Amount
            </Typography>
          </Card>
        </Grid>
      </Grid> */}

      {/* Filters */}
      <Card sx={{ mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) => {
                  setFilters({ ...filters, fromDate: e.target.value });
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) => {
                  setFilters({ ...filters, toDate: e.target.value });
                }}
              />
            </Grid>
            {/* <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Cancel Reason</InputLabel>
                <Select 
                  label="Cancel Reason" 
                  value={filters.cancelReason}
                  onChange={(e) => {
                    setFilters({ ...filters, cancelReason: e.target.value });
                  }}
                >
                  <MenuItem value="">All Reasons</MenuItem>
                  {cancelReasons.map((reason) => (
                    <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={associates}
                loading={associatesLoading}
                getOptionLabel={(opt) => {
                  if (!opt) return '';
                  if (typeof opt === 'string') return opt;
                  const name = opt.name || 'N/A';
                  const mobile = opt.phone || opt.mobile || opt.whatsapp || '';
                  return mobile ? `${name} — ${mobile}` : `${name}`;
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                filterOptions={(options) => options}
                value={selectedAssociate}
                inputValue={associateSearchInput}
                onInputChange={(event, newInputValue, reason) => {
                  if (event && event.type === 'blur') {
                    return;
                  }
                  setAssociateSearchInput(newInputValue);
                  if (reason === 'reset') return;

                  if (associateSearchTimeoutRef.current) {
                    clearTimeout(associateSearchTimeoutRef.current);
                  }
                  associateSearchTimeoutRef.current = setTimeout(() => {
                    fetchAssociates(newInputValue);
                  }, 500);
                }}
                onChange={(_, newValue) => {
                  setSelectedAssociate(newValue);
                  setFilters({ ...filters, associate: newValue ? newValue.name : '' });
                  setAssociateSearchInput(newValue ? (newValue.phone ? `${newValue.name} — ${newValue.phone}` : newValue.name) : "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associate"
                    placeholder="Search associate..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {associatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name || 'N/A'}
                      </Typography>
                      {(option.phone || option.mobile || option.whatsapp || option.email) && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                          {option.phone || option.mobile || option.whatsapp || option.email}
                          {option.associateId && ` • ID: ${option.associateId}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText={associatesLoading ? "Loading associates..." : "No associates found"}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2.4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: theme.palette.primary.main,
                    flex: 1,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#9e9e9e',
                    }
                  }}
                >
                  {searchLoading ? '...' : 'Search'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: '#f0f7ff',
                    }
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                size="small"
                options={customerSuggestions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name || '';
                }}
                onInputChange={(_, newValue) => {
                  setFilters({ ...filters, searchTerm: newValue });
                }}
                value={filters.searchTerm || null}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    setFilters({ ...filters, searchTerm: newValue });
                  } else if (newValue) {
                    setFilters({ ...filters, searchTerm: newValue.name });
                  } else {
                    setFilters({ ...filters, searchTerm: '' });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search by Customer Name, Phone, Booking ID, or Property No"
                    placeholder="Type to search..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.25 }}>
                        {option.phone} • {option.bookingId} • {option.plotNo}
                      </Typography>
                    </Box>
                  </Box>
                )}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  const searchLower = inputValue.toLowerCase();
                  return options.filter(option =>
                    option.name.toLowerCase().includes(searchLower) ||
                    option.phone.includes(inputValue) ||
                    option.bookingId.toLowerCase().includes(searchLower) ||
                    option.plotNo.toLowerCase().includes(searchLower) ||
                    (option.associate && option.associate.toLowerCase().includes(searchLower))
                  );
                }}
                noOptionsText="No customers found"
                clearOnEscape
                selectOnFocus
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Cancelled Bookings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {filteredData.length} booking{filteredData.length !== 1 ? 's' : ''} found
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Cancel Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Property Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Paid Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Refund Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Cancel Reason</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Associate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookingsLoading ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Loading cancelled bookings...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((booking) => (
                    <TableRow
                      key={booking.sno}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' }
                      }}
                    >
                      <TableCell>{booking.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{booking.bookingId}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{booking.bookingDate}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#d32f2f' }}>{booking.cancelDate}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{booking.customerName}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{booking.plotNo}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>₹{booking.plotAmount.toLocaleString()}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>₹{booking.paidAmount.toLocaleString()}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>₹{booking.refundAmount.toLocaleString()}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Tooltip title={booking.cancelReason} arrow placement="top">
                          <Chip
                            label={booking.cancelReason}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>{booking.associate}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleView(booking)}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {hasSearched ? 'No cancelled bookings found' : 'Click Search to view cancelled bookings'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              
              component="div"
              count={hasSearched ? totalBookings : filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={async (event, newPage) => {
                setPage(newPage);
                if (hasSearched) {
                  await fetchCancelledBookings({ page: newPage + 1 });
                }
              }}
              onRowsPerPageChange={async (event) => {
                const newRowsPerPage = parseInt(event.target.value, 10);
                setRowsPerPage(newRowsPerPage);
                setPage(0);
                if (hasSearched) {
                  await fetchCancelledBookings({ page: 1, limit: newRowsPerPage });
                }
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility />
            <Typography variant="h6" fontWeight="bold">
              Cancelled Booking Details
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedBooking && (
            <>
              {/* Header Section */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedBooking.customerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Booking ID: {selectedBooking.bookingId}
                  </Typography>
                  <Chip
                    label="Cancelled"
                    size="small"
                    color="error"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={viewTabValue}
                  onChange={(e, newValue) => setViewTabValue(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      minHeight: 48
                    }
                  }}
                >
                  <Tab label="Booking Details" icon={<Home />} iconPosition="start" />
                  <Tab
                    icon={<CurrencyRupeeIcon />}
                    iconPosition="start"
                    label="Refund History"
                  />
                </Tabs>
              </Box>

              {/* Tab Content */}
              {viewTabValue === 0 && (
                <Box>
                  {/* Booking Information */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                      Booking Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Booking ID" value={selectedBooking.bookingId || ''} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Booking Date" type="date" value={selectedBooking.bookingDate || ''} disabled InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Property Number" value={selectedBooking.plotNo || ''} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Branch" value={selectedBooking.branchName || 'Not Available'} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Payment Plan" value={selectedBooking.paymentPlan || 'Not Available'} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Status" value={selectedBooking.status || 'Cancelled'} disabled />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Customer Information */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person color="primary" />
                      Customer Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Customer Name" value={selectedBooking.customerName || ''} disabled />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          value={selectedBooking.customerPhone || ''}
                          disabled
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Phone fontSize="small" /></InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Associate Information */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business color="primary" />
                      Associate Information
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Associate/Agent" value={selectedBooking.associate || 'Not Available'} disabled />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Financial Summary */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                      Financial Summary
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="text.primary">
                            ₹{(selectedBooking.plotAmount || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Total Property Amount</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="success.main">
                            ₹{(selectedBooking.paidAmount || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Paid Amount</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="warning.main">
                            ₹{((selectedBooking.plotAmount || 0) - (selectedBooking.paidAmount || 0)).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Remaining Amount</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#ffebee', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight="bold" color="error.main">
                            ₹{(selectedBooking.cancellationCharge || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Cancellation Charge</Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Payment Progress */}
                    {(selectedBooking.plotAmount || 0) > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
                          Payment Progress
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight="600">
                            {(((selectedBooking.paidAmount || 0) / (selectedBooking.plotAmount || 1)) * 100).toFixed(1)}% Completed
                          </Typography>
                          <Typography variant="body2" fontWeight="600">
                            ₹{(selectedBooking.paidAmount || 0).toLocaleString()} / ₹{(selectedBooking.plotAmount || 0).toLocaleString()}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={((selectedBooking.paidAmount || 0) / (selectedBooking.plotAmount || 1)) * 100}
                          color="primary"
                          sx={{ height: 10, borderRadius: 5 }}
                        />
                      </Box>
                    )}
                  </Paper>

                  {/* Cancellation Details */}
                  <Paper sx={{ p: 3, borderRadius: 2, mb: 3, bgcolor: '#ffebee', border: '1px solid #f44336' }}>
                    <Typography variant="h6" fontWeight="bold" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Warning color="error" />
                      Cancellation Details
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: '#f44336' }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Cancel Date</Typography>
                          <Typography variant="body1" fontWeight="600" color="error.main">
                            {selectedBooking.cancelDate || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                      {selectedBooking.cancelReason && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Cancel Reason</Typography>
                            <Chip
                              label={selectedBooking.cancelReason}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6} md={4}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Cancelled By</Typography>
                          <Typography variant="body1" fontWeight="600">
                            {selectedBooking.cancelledBy || 'Not Specified'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                </Box>
              )}

              {/* Tab 1: Refund Receipt */}
              {viewTabValue === 1 && (
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt color="primary" />
                    Refund History
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {/* Refund Information */}
                  <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CurrencyRupeeIcon color="success" />
                      Refund Information
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: '#4caf50' }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Cancellation Charge (%)"
                          type="number"
                          disabled
                          value={cancellationChargePercent}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          helperText={selectedBooking.cancellationCharge ? `Amount: ₹${selectedBooking.cancellationCharge.toLocaleString()}` : ''}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Refund Amount"
                          value={selectedBooking.refundAmount || 0}
                          disabled
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              color: 'success.main',
                              fontWeight: 600,
                              fontSize: '1.1rem'
                            }
                          }}
                        />
                      </Grid>
                      {selectedBooking.refundMethod && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Refund Method"
                            value={selectedBooking.refundMethod}
                            disabled
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  {/* Refund Summary */}
                  {(selectedBooking?.refunds && selectedBooking.refunds.length > 0) && (
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid #ffcc80' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="600">
                              Total Refund
                            </Typography>
                            <MoneyOff color="error" />
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="error.main">
                            ₹{selectedBooking.refunds.reduce((sum, r) => sum + (r.amount || 0), 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedBooking.refunds.length} record(s)
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid #81c784' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="600">
                              Processed
                            </Typography>
                            <CheckCircle color="success" />
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="success.main">
                            ₹{selectedBooking.refunds
                              .filter(r => r.status === 'Processed')
                              .reduce((sum, r) => sum + (r.amount || 0), 0)
                              .toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedBooking.refunds.filter(r => r.status === 'Processed').length} receipt(s)
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, bgcolor: '#fff9c4', border: '1px solid #ffd54f' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="600">
                              Pending
                            </Typography>
                            <Pending color="warning" />
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="warning.main">
                            ₹{selectedBooking.refunds
                              .filter(r => r.status === 'Pending')
                              .reduce((sum, r) => sum + (r.amount || 0), 0)
                              .toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedBooking.refunds.filter(r => r.status === 'Pending').length} receipt(s)
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2, bgcolor: '#f5f5f5', border: '1px solid #bdbdbd' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="600">
                              Total Paid
                            </Typography>
                            <CurrencyRupeeIcon color="action" />
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="text.primary">
                            ₹{(selectedBooking.paidAmount || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Paid Amount
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  )}



                  {/* Refund Receipt Table */}
                  {/* <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell><strong>Receipt No</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell><strong>Amount</strong></TableCell>
                          <TableCell><strong>Payment Mode</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Remarks</strong></TableCell>
                          <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedBooking?.refunds && selectedBooking.refunds.length > 0 ? (
                          selectedBooking.refunds.map((refund, refundIndex) => (
                            <TableRow key={refundIndex} sx={{ bgcolor: '#fff3e0' }}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="600">
                                  {refund.receiptNo}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {refund.refundDate || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="600" color="error.main">
                                  ₹{refund.amount.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {refund.method || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={refund.status}
                                  size="small"
                                  color={
                                    refund.status === 'Processed' ? 'success' :
                                      refund.status === 'Pending' ? 'warning' :
                                        'error'
                                  }
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {refund.remarks || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="Print Refund Record">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const printWindow = window.open('', '_blank');
                                        if (!printWindow) return;
                                        const style = `
                                        <style>
                                          body { font-family: Arial, sans-serif; padding: 20px; }
                                          h1 { color: #d32f2f; margin-bottom: 20px; }
                                          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                                          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
                                          th { background: #f5f5f5; font-weight: bold; }
                                          .header { background: #fff3e0; padding: 15px; border-radius: 5px; }
                                        </style>
                                      `;
                                        printWindow.document.write(`
                                        <html>
                                          <head>
                                            <title>Refund Record - ${refund.receiptNo}</title>
                                            ${style}
                                          </head>
                                          <body>
                                            <div class="header">
                                              <h1>REFUND RECORD</h1>
                                              <p><strong>Receipt No:</strong> ${refund.receiptNo}</p>
                                              <p><strong>Booking ID:</strong> ${selectedBooking.bookingId}</p>
                                              <p><strong>Customer Name:</strong> ${selectedBooking.customerName}</p>
                                              <p><strong>Property Number:</strong> ${selectedBooking.plotNo}</p>
                                              <p><strong>Refund Date:</strong> ${refund.refundDate || '-'}</p>
                                              <p><strong>Amount:</strong> ₹${refund.amount.toLocaleString()}</p>
                                              <p><strong>Payment Mode:</strong> ${refund.method || '-'}</p>
                                              <p><strong>Status:</strong> ${refund.status}</p>
                                              ${refund.remarks ? `<p><strong>Remarks:</strong> ${refund.remarks}</p>` : ''}
                                            </div>
                                            <p style="margin-top: 30px; color: #666;">This is a system generated refund record.</p>
                                          </body>
                                        </html>
                                      `);
                                        printWindow.document.close();
                                        printWindow.focus();
                                        printWindow.print();
                                      }}
                                    >
                                      <Print fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download Refund PDF">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const printWindow = window.open('', '_blank');
                                        if (!printWindow) return;
                                        const style = `
                                        <style>
                                          body { font-family: Arial, sans-serif; padding: 20px; }
                                          h1 { color: #d32f2f; margin-bottom: 20px; }
                                          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                                          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
                                          th { background: #f5f5f5; font-weight: bold; }
                                          .header { background: #fff3e0; padding: 15px; border-radius: 5px; }
                                        </style>
                                      `;
                                        printWindow.document.write(`
                                        <html>
                                          <head>
                                            <title>Refund Record - ${refund.receiptNo}</title>
                                            ${style}
                                          </head>
                                          <body>
                                            <div class="header">
                                              <h1>REFUND RECORD</h1>
                                              <p><strong>Receipt No:</strong> ${refund.receiptNo}</p>
                                              <p><strong>Booking ID:</strong> ${selectedBooking.bookingId}</p>
                                              <p><strong>Customer Name:</strong> ${selectedBooking.customerName}</p>
                                              <p><strong>Property Number:</strong> ${selectedBooking.plotNo}</p>
                                              <p><strong>Refund Date:</strong> ${refund.refundDate || '-'}</p>
                                              <p><strong>Amount:</strong> ₹${refund.amount.toLocaleString()}</p>
                                              <p><strong>Payment Mode:</strong> ${refund.method || '-'}</p>
                                              <p><strong>Status:</strong> ${refund.status}</p>
                                              ${refund.remarks ? `<p><strong>Remarks:</strong> ${refund.remarks}</p>` : ''}
                                            </div>
                                            <p style="margin-top: 30px; color: #666;">This is a system generated refund record.</p>
                                          </body>
                                        </html>
                                      `);
                                        printWindow.document.close();
                                        printWindow.focus();
                                        printWindow.print();
                                      }}
                                    >
                                      <PictureAsPdf fontSize="small" color="error" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                No refund records available.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer> */}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CancelReport;